import { ExternalLink } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { DateCell } from '../../../../components/table/DateCell';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { formatDateOnly } from '../../../../lib/formatDate';
import { myTrainingRecommendationsApi } from '../myTrainingRecommendationsApi';
import {
  MY_TRAINING_DECISION_LABELS,
  type MyTrainingRecommendation,
  type MyTrainingRecommendationAccount,
} from '../myTrainingRecommendationTypes';

/**
 * Self Service › Learning & Development › Training Recommendations — every training
 * recommendation sent to the signed-in employee, across every survey, in one register.
 *
 * **IT LISTS ONLY THE CALLER'S OWN, and nothing here says so to the server.** Whose is fixed by
 * the ROUTE; there is no scope parameter on the wire.
 *
 * **Nothing to create, edit or delete**: a recommendation is written by Learning & Development on
 * a development plan. The anchor opens the recommendation's profile — the one a survey's own
 * Training Recommendations table opens — which is where the employee answers it and records its
 * training, on Survey's keys, not this module's one read. Its report is the survey's Preview › Report.
 */
export function MyTrainingRecommendationsIndexPage() {
  const navigate = useNavigate();

  const [account, setAccount] = useState<MyTrainingRecommendationAccount | null>(null);

  /*
    A refusal on the ACCOUNT is announced before it fires: an account linked to no employee
    record is sent no recommendation. Seeded met — a lookup that never answers states nothing.
  */
  useEffect(() => {
    const controller = new AbortController();

    myTrainingRecommendationsApi
      .me(controller.signal)
      .then(setAccount)
      .catch(() => undefined);

    return () => controller.abort();
  }, []);

  const fetchRows = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      myTrainingRecommendationsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          decision: query.facets.decision?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (row: MyTrainingRecommendation): MenuItem[] => [
      {
        // Carries the key of the page it LANDS on — another module's.
        key: `survey-${row.id}`,
        label: 'Open survey',
        icon: <ExternalLink className="h-4 w-4" aria-hidden="true" />,
        permission: 'my-surveys.read',
        onClick: () => navigate(`/self-service/surveys/${row.surveyId}`),
      },
    ],
    [navigate],
  );

  return (
    <IndexPage<MyTrainingRecommendation>
      module="my-training-recommendations"
      breadcrumb={['Self Service', 'Learning & Development', 'Training Recommendations']}
      columns={COLUMNS}
      facets={[
        { key: 'decision', label: 'Your answer' },
        { key: 'status', label: 'Status' },
      ]}
      // The writes behind these rows: the employee's track on a recommendation, and its report's files.
      auditEntityTypes={['SurveyTrainingRecommendationRecipient', 'SurveyTrainingRecommendationReportAttachment']}
      rowKey={(row) => row.id}
      fetch={fetchRows}
      searchPlaceholder="Search recommendations and surveys"
      emptyMessage="No training has been recommended to you yet. One appears here when Learning & Development sends it to you."
      prerequisites={[
        {
          label: 'your employee record',
          met: account?.error == null,
          detail: account?.error ?? undefined,
          to: '/personnel',
          permission: 'personnel.update',
        },
      ]}
      // The TITLE opens the recommendation — a page, so the breadcrumb carries the way back.
      onRowOpen={(row) => navigate(`/self-service/training-recommendations/${row.id}`)}
      openPermission="my-training-recommendations.read"
      rowActions={rowActions}
      // The standard checkbox gutter, as on the employee's Survey list — every index page looks alike.
      selectable
      // No provenance: the row is the employee's track, and who wrote, approved or sent the
      // recommendation is Learning & Development's — the call the survey's own table makes.
      otherInfo={() => null}
      auditColumns={false}
    />
  );
}

const COLUMNS: ColumnDef<MyTrainingRecommendation>[] = [
  {
    key: 'title',
    header: 'Title',
    width: 320,
    flex: true,
    sortable: true,
    render: (row) => <TextCell value={row.title} />,
  },
  {
    // Where it was recommended from — the column a survey's own tab does not need.
    key: 'surveyTitle',
    header: 'Survey',
    width: 260,
    sortable: true,
    summary: true,
    render: (row) => <TextCell value={row.surveyTitle} />,
  },
  {
    key: 'sentAt',
    header: 'Sent',
    width: 170,
    sortable: true,
    summary: true,
    render: (row) => <DateCell value={row.sentAt} />,
  },
  {
    // Two calendar dates as one range — `formatDateOnly`, never the instant path.
    key: 'training',
    header: 'Training dates',
    width: 230,
    render: (row) => (
      <TextCell
        value={
          row.trainingStartDate != null && row.trainingEndDate != null
            ? `${formatDateOnly(row.trainingStartDate)} – ${formatDateOnly(row.trainingEndDate)}`
            : null
        }
        mono
      />
    ),
  },
  {
    key: 'completed',
    header: 'Ended',
    width: 170,
    render: (row) => <DateCell value={row.trainingCompletedAt ?? null} fallback="—" />,
  },
  {
    key: 'report',
    header: 'Report Submitted',
    width: 170,
    render: (row) => <DateCell value={row.reportAt ?? null} fallback="—" />,
  },
  {
    // The reader's own answer, beside the row's live-or-deleted pill: they are different facts.
    // Keyed apart from `status`, and the same three values the filter offers.
    key: 'decision',
    header: 'Your answer',
    width: 190,
    stage: true,
    render: (row) => <StatusText tone="inactive">{MY_TRAINING_DECISION_LABELS[row.decision]}</StatusText>,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    // The row is live while the SURVEY it came from is — a deleted recommendation never arrives.
    render: (row) =>
      row.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Survey deleted</StatusText>,
  },
];
